import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import DashboardShell from "../components/common/DashboardShell";
import { LeagueApi } from "../utils/leagueApi";

const STATUS_BADGE = {
  draft: "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400",
  valid: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
  invalid: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
  locked: "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300",
};

// A single submission: editable draft, or a frozen read-only locked view.
function SubmissionCard({ submission, onSaved, onLockRequest }) {
  const [repoUrl, setRepoUrl] = useState(submission.repo_url || "");
  const [videoUrl, setVideoUrl] = useState(submission.video_url || "");
  const [busy, setBusy] = useState(false);

  const badge = STATUS_BADGE[submission.status] || STATUS_BADGE.draft;

  const save = async () => {
    setBusy(true);
    await LeagueApi.updateSubmission(submission.id, {
      team: submission.team,
      hackathon: submission.hackathon,
      repo_url: repoUrl,
      video_url: videoUrl,
    }).catch(() => undefined);
    setBusy(false);
    onSaved();
  };

  const validate = async () => {
    setBusy(true);
    await LeagueApi.validateSubmission(submission.id).catch(() => undefined);
    setBusy(false);
    onSaved();
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-semibold text-gray-800 dark:text-gray-100">
            {submission.team_name}
          </span>
          <span className="text-gray-400 dark:text-gray-500">
            {" "}
            → {submission.hackathon_name}
          </span>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${badge}`}
        >
          {submission.status}
        </span>
      </div>

      {submission.is_locked ? (
        <div className="mt-3 space-y-2 text-sm">
          <p className="text-gray-600 dark:text-gray-400">
            Repo:{" "}
            <a
              className="text-blue-600 hover:underline"
              href={submission.repo_url}
              rel="noreferrer"
              target="_blank"
            >
              {submission.repo_url}
            </a>
          </p>
          {submission.video_url ? (
            <p className="text-gray-600 dark:text-gray-400">
              Video:{" "}
              <a
                className="text-blue-600 hover:underline"
                href={submission.video_url}
                rel="noreferrer"
                target="_blank"
              >
                {submission.video_url}
              </a>
            </p>
          ) : null}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              Locked roster ({submission.roster_snapshot.length})
            </p>
            <div className="mt-1 flex flex-wrap gap-1">
              {submission.roster_snapshot.map((m) => (
                <span
                  key={m.user_id}
                  className="rounded bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-400"
                >
                  {m.name}
                  {m.is_captain ? " (captain)" : ""}
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Locked — this submission is final and read-only.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <input
            className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
            placeholder="Repository URL (github/gitlab/bitbucket)"
            type="url"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
          />
          <input
            className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
            placeholder="Demo video URL (optional)"
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
          />
          {submission.validation_notes ? (
            <p className="text-xs text-red-600">
              {submission.validation_notes}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              className="rounded border px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 disabled:opacity-50"
              disabled={busy}
              type="button"
              onClick={save}
            >
              Save
            </button>
            <button
              className="rounded border border-blue-200 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
              disabled={busy}
              type="button"
              onClick={validate}
            >
              Validate
            </button>
            {submission.status === "valid" ? (
              <button
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                type="button"
                onClick={() => onLockRequest(submission)}
              >
                Submit Final Work
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

SubmissionCard.propTypes = {
  submission: PropTypes.object.isRequired,
  onSaved: PropTypes.func.isRequired,
  onLockRequest: PropTypes.func.isRequired,
};

// Confirm-lock modal: locking is irreversible, so make the user confirm.
function ConfirmLockModal({ submission, onCancel, onConfirm }) {
  if (!submission) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Submit final work?
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          This locks <strong>{submission.team_name}</strong>&rsquo;s entry for{" "}
          <strong>{submission.hackathon_name}</strong>. The roster is frozen and
          the submission becomes read-only. This cannot be undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            className="rounded border px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            type="button"
            onClick={() => onConfirm(submission)}
          >
            Lock &amp; submit
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmLockModal.propTypes = {
  submission: PropTypes.object,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default function SubmissionPortal() {
  const [submissions, setSubmissions] = useState([]);
  const [options, setOptions] = useState({ teams: [], hackathons: [] });
  const [form, setForm] = useState({ team: "", hackathon: "", repo_url: "" });
  const [lockTarget, setLockTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [subs, opts] = await Promise.all([
      LeagueApi.listSubmissions(),
      LeagueApi.submissionOptions(),
    ]);
    setSubmissions(subs);
    setOptions(opts);
  };

  useEffect(() => {
    load()
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const reload = () => {
    load().catch(() => undefined);
  };

  const create = async () => {
    if (!form.team || !form.hackathon) return;
    await LeagueApi.createSubmission(form).catch(() => undefined);
    setForm({ team: "", hackathon: "", repo_url: "" });
    reload();
  };

  const confirmLock = async (submission) => {
    await LeagueApi.lockSubmission(submission.id).catch(() => undefined);
    setLockTarget(null);
    reload();
  };

  return (
    <DashboardShell>
      <div className="mx-auto max-w-3xl space-y-8 p-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Submissions
        </h1>

        {loading ? (
          <p className="py-8 text-center text-gray-400 dark:text-gray-500">
            Loading…
          </p>
        ) : (
          <>
            <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
              <h2 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
                Start a submission
              </h2>
              {options.teams.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  You need to be on a team to submit. Join or create one in the
                  Free Agent Hub.
                </p>
              ) : (
                <div className="space-y-3">
                  <select
                    className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                    value={form.team}
                    onChange={(e) => setForm({ ...form, team: e.target.value })}
                  >
                    <option value="">Select your team…</option>
                    {options.teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                    value={form.hackathon}
                    onChange={(e) =>
                      setForm({ ...form, hackathon: e.target.value })
                    }
                  >
                    <option value="">Select a hackathon…</option>
                    {options.hackathons.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.name} — {h.chapter}
                      </option>
                    ))}
                  </select>
                  <input
                    className="w-full rounded border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm"
                    placeholder="Repository URL (optional now, required to validate)"
                    type="url"
                    value={form.repo_url}
                    onChange={(e) =>
                      setForm({ ...form, repo_url: e.target.value })
                    }
                  />
                  <button
                    className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    type="button"
                    onClick={create}
                  >
                    Create draft
                  </button>
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h2 className="font-semibold text-gray-700 dark:text-gray-300">
                Your submissions
              </h2>
              {submissions.length === 0 ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  No submissions yet.
                </p>
              ) : (
                submissions.map((submission) => (
                  <SubmissionCard
                    key={submission.id}
                    submission={submission}
                    onLockRequest={setLockTarget}
                    onSaved={reload}
                  />
                ))
              )}
            </section>
          </>
        )}
      </div>

      <ConfirmLockModal
        submission={lockTarget}
        onCancel={() => setLockTarget(null)}
        onConfirm={confirmLock}
      />
    </DashboardShell>
  );
}
